import { useState } from "react";
import { router } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import useGoBack from "@/hooks/use_go_back";
import {
  Dialog,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import { Row, Stack } from "@/sprout/layout";
import ErrorIcon from "@/sprout/icons/error";
import { Body, SectionTitle } from "@/sprout/typography";
import QuestionIcon from "@/sprout/icons/question";
import {
  DestructiveButton,
  PrimaryButton,
  SecondaryButton,
  TertiaryDangerButton,
} from "@sprout/buttons";

type Props = {
  loading: boolean;
  error: string | null;
  submit: () => void;
  paths: {
    userPath: string;
  };
  showDelete?: boolean;
  onCancel?: () => void;
};

const ProfileButtons = (props: Props) => {
  const { loading, error, submit, paths, showDelete = true, onCancel } = props;
  const [deletionError, setDeletionError] = useState<string | null>(null);

  const { t } = useTranslation("user");
  const goBack = useGoBack();

  const onDelete = async () => {
    try {
      router.delete(paths.userPath);
    } catch (_e) {
      setDeletionError(t("profileButtons.error"));
    }
  };

  return (
    <div>
      <div className="submit-buttons-stack">
        <PrimaryButton onClick={submit} loading={loading}>
          {t("profileButtons.save")}
        </PrimaryButton>
        <SecondaryButton
          onClick={() => {
            onCancel?.();
            goBack();
          }}
        >
          {t("profileButtons.cancel")}
        </SecondaryButton>
        {showDelete && (
          <div className="profile__delete">
            <Dialog>
              <DialogTrigger>
                <TertiaryDangerButton>{t("profileButtons.delete")}</TertiaryDangerButton>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[400px]">
                <DialogHeader>
                  <DialogTitle>
                    <Row gap="16px" align="center">
                      <ErrorIcon color="error-red" size="xs" />
                      <SectionTitle medium>{t("profileButtons.modal.title")}</SectionTitle>
                    </Row>
                  </DialogTitle>
                </DialogHeader>
                <DialogDescription className="support-modal__body">
                  <Stack gap="16px">
                    <Body>{t("profileButtons.modal.body")}</Body>
                    <Row gap="16px" className="p-4 rounded-[5px] bg-pale-blue flex-grow-0">
                      <div>
                        <QuestionIcon color="blue" size="md" />
                      </div>
                      <Body>{t("profileButtons.modal.callout")}</Body>
                    </Row>
                  </Stack>
                </DialogDescription>
                <DialogFooter>
                  <DialogClose>
                    <SecondaryButton>{t("profileButtons.modal.cancel")}</SecondaryButton>
                  </DialogClose>
                  <DialogClose>
                    <DestructiveButton onClick={onDelete}>
                      {t("profileButtons.modal.confirm")}
                    </DestructiveButton>
                  </DialogClose>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>
      {(error || deletionError) && (
        <p id="profile-error" className="helper-text helper-text--error" aria-live="polite">
          {error || deletionError}
        </p>
      )}
    </div>
  );
};

export default ProfileButtons;
